import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Dashboard,
  Add,
  Warning,
  TrendingUp,
  TrendingDown,
  Speed,
  Assessment,
} from '@mui/icons-material';
import { useTenant } from '../hooks/useTenant';

const Dashboards: React.FC = () => {
  const { currentTenant, isTenantSelected } = useTenant();

  if (!isTenantSelected) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" icon={<Warning />}>
          Selecione um tenant para visualizar os dashboards
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, ml: 12.5 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Dashboard />
          Dashboards
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualizando métricas do tenant: <strong>{currentTenant?.name}</strong>
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="success.main">
                    1,234
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Processos Executados
                  </Typography>
                </Box>
                <TrendingUp color="success" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={75}
                sx={{ mt: 1 }}
                color="success"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="primary.main">
                    98.5%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de Sucesso
                  </Typography>
                </Box>
                <Speed color="primary" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={98.5}
                sx={{ mt: 1 }}
                color="primary"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="warning.main">
                    45s
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tempo Médio
                  </Typography>
                </Box>
                <Assessment color="warning" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={60}
                sx={{ mt: 1 }}
                color="warning"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="error.main">
                    12
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Falhas Hoje
                  </Typography>
                </Box>
                <TrendingDown color="error" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={15}
                sx={{ mt: 1 }}
                color="error"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dashboard Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Geral
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Métricas de performance dos processos em tempo real.
              </Typography>
              <Chip label="Em desenvolvimento" color="warning" size="small" />
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<Add />} disabled>
                Configurar
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Análise de Erros
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Dashboard para monitoramento e análise de erros.
              </Typography>
              <Chip label="Em desenvolvimento" color="warning" size="small" />
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<Add />} disabled>
                Configurar
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Uso de Recursos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Monitoramento de CPU, memória e outros recursos.
              </Typography>
              <Chip label="Em desenvolvimento" color="warning" size="small" />
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<Add />} disabled>
                Configurar
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Relatórios Customizados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Crie relatórios personalizados com suas métricas.
              </Typography>
              <Chip label="Em desenvolvimento" color="warning" size="small" />
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<Add />} disabled>
                Configurar
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Coming Soon */}
      <Paper sx={{ p: 3, mt: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          📊 Dashboards em Desenvolvimento
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Os dashboards estão sendo desenvolvidos e estarão disponíveis em breve.
          Aqui você poderá visualizar métricas, performance e insights dos seus processos.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Funcionalidades planejadas: Gráficos interativos, alertas personalizados,
          exportação de relatórios e integração com ferramentas externas.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Dashboards;
